import { BehaviorSubject, Observable } from 'rxjs';

import { type SegmentOptions } from 'cellpose-js';
import { CellposeSegmenterService } from '@jax-data-science/sci-image-visualizer';

import { ProcessingEngine } from '../../engine.model';
import { OperationDescriptor } from '../../operation.model';
import { ProcessingImage } from '../../processing-image';
import { CellPoseChannelMode } from '../../../request';

/** Progress snapshot for an in-flight model download. `null` = no active download. */
export interface ModelDownloadProgress {
  /** URL being fetched. */
  modelUrl: string;
  /** Bytes downloaded so far. */
  loaded: number;
  /** Total bytes if Content-Length was set, else null. */
  total: number | null;
  /** Convenience: percentage 0–100, or null if total is unknown. */
  pct: number | null;
}

/**
 * Cellpose-SAM segmentation engine.
 *
 * Wraps the `cellpose-js` package. The package already encapsulates its own
 * ORT-WebGPU inference worker (preprocess on the main thread, inference on a
 * dedicated worker, dynamics on the main thread), so this engine does NOT
 * spawn an additional worker — that would be a redundant hop.
 *
 * Model URL: defaults to the public HF Hub upload at
 *   https://huggingface.co/ballon999/cellpose-sam-onnx/resolve/main/cpsam_fp16.onnx
 * Cached in IndexedDB after the first visit.
 */

const DEFAULT_MODEL_URL =
  'https://huggingface.co/ballon999/cellpose-sam-onnx/resolve/main/cpsam_fp16.onnx';

/** Distinct color per instance via golden-ratio hue rotation. */
function colorFor(label: number): [number, number, number] {
  if (label === 0) return [0, 0, 0];
  const hue = (label * 137.508) % 360;
  const c = 0.7;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  let r = 0, g = 0, b = 0;
  if      (hue <  60) { r = c; g = x; }
  else if (hue < 120) { r = x; g = c; }
  else if (hue < 180) { g = c; b = x; }
  else if (hue < 240) { g = x; b = c; }
  else if (hue < 300) { r = x; b = c; }
  else                { r = c; b = x; }
  return [Math.round(255 * r), Math.round(255 * g), Math.round(255 * b)];
}

/** Blend the source RGBA image with a per-instance color overlay. */
function blendMaskOverlay(
  src: ProcessingImage,
  masks: Uint32Array,
  alpha = 0.55
): ProcessingImage {
  const src4 = src.channels === 4 ? src.data : src.toImageData().data;
  const out = new Uint8ClampedArray(src.width * src.height * 4);
  const inv = 1 - alpha;
  for (let i = 0; i < src.width * src.height; i++) {
    const label = masks[i]!;
    if (label === 0) {
      out[i * 4]     = src4[i * 4]     as number;
      out[i * 4 + 1] = src4[i * 4 + 1] as number;
      out[i * 4 + 2] = src4[i * 4 + 2] as number;
    } else {
      const [r, g, b] = colorFor(label);
      out[i * 4]     = Math.round((src4[i * 4]     as number) * inv + r * alpha);
      out[i * 4 + 1] = Math.round((src4[i * 4 + 1] as number) * inv + g * alpha);
      out[i * 4 + 2] = Math.round((src4[i * 4 + 2] as number) * inv + b * alpha);
    }
    out[i * 4 + 3] = 255;
  }
  return new ProcessingImage(src.width, src.height, 4, out);
}

export class CellposeEngine implements ProcessingEngine {
  readonly id = 'cellpose';
  readonly name = 'Cellpose-SAM';
  /**
   * The cellpose-js engine is a **preview provider**, not a picker entry.
   *
   * Cellpose-SAM appears in the pipeline operation picker as a regular JIT
   * server tool (`operationId='cellpose-sam'`, `engineId='server'`, sourced
   * from {@link JIT_TOOLS}). The cellpose-sam tool declares
   * `previewEngineId: 'cellpose'`, which the pipeline executor uses to
   * delegate local preview to this engine's `execute()` instead of
   * passing-through (as it would for any other server-engine step).
   *
   * On Run-on-Server the step is just another JIT instruction
   * (`CellPoseSamInstruction`) — no special branch is needed in the dialog.
   */
  readonly operations: OperationDescriptor[] = [];

  private _initialized = false;
  /** Aborter for the in-flight segment() call. */
  private _abort: AbortController | null = null;
  /** Public progress feed for first-time model downloads. `null` between downloads. */
  private _progress = new BehaviorSubject<ModelDownloadProgress | null>(null);
  readonly progress$: Observable<ModelDownloadProgress | null> = this._progress.asObservable();
  /** Public status feed: human-readable phase string. `null` when idle. */
  private _status = new BehaviorSubject<string | null>(null);
  readonly status$: Observable<string | null> = this._status.asObservable();

  /** The cellpose-SAM model/worker is owned by the library's shared
   *  {@link CellposeSegmenterService}, so the pipeline and the toolbar's
   *  automatic Cellpose tool reuse one loaded model + WebGPU session + worker. */
  constructor(private readonly cellpose: CellposeSegmenterService) {}

  isInitialized(): boolean {
    return this._initialized;
  }

  async initialize(): Promise<void> {
    // The 588 MB model + ORT runtime are loaded lazily by the shared service on
    // first execute() — opening the pipeline dialog triggers no download.
    this._initialized = true;
  }

  cancelCurrentOperation(): void {
    this._abort?.abort();
    this._abort = null;
  }

  /** Load (or reuse) the shared Cellpose instance, pumping the engine's
   *  progress/status feeds for the pipeline dialog. */
  private async loadModel(): Promise<Awaited<ReturnType<CellposeSegmenterService['getModel']>>> {
    let lastPct = -1;
    this._status.next('Loading Cellpose-SAM model…');
    try {
      return await this.cellpose.getModel((loaded, total) => {
        const pct = total ? Math.floor((loaded / total) * 100) : null;
        if (pct !== null && pct === lastPct) return;
        if (pct !== null) lastPct = pct;
        this._progress.next({ modelUrl: DEFAULT_MODEL_URL, loaded, total, pct });
        if (loaded > 0 && this._status.value === 'Loading Cellpose-SAM model…') {
          this._status.next('Downloading model (588 MB)…');
        }
      });
    } finally {
      this._progress.next(null);
    }
  }

  async execute(
    _operation: OperationDescriptor,
    input: ProcessingImage,
    params: Record<string, any>
  ): Promise<ProcessingImage> {
    if (!this._initialized) await this.initialize();

    // Arm the abort controller BEFORE any await so cancellation can reach the
    // segment() call and there's no race window where `_abort` is null but the
    // engine is busy.
    this._abort = new AbortController();
    const signal = this._abort.signal;

    // Reuse the library's shared cellpose-SAM instance (loads once across the
    // pipeline + the toolbar Cellpose tool).
    const cp = await this.loadModel();

    // If the user cancelled while the model was loading, bail before invoking
    // cp.segment. cellpose-js's segment() also throws "Aborted before start"
    // if the signal is already aborted, but checking here surfaces the abort
    // before we touch any cp state and skips the "Preprocessing image…" status.
    if (signal.aborted) {
      throw new DOMException('Aborted before start', 'AbortError');
    }

    const diameter = Number(params.diameter) || undefined;
    // Map the CellPoseSam-vocabulary params (channelMode + 1-based cytoChannel /
    // nucChannel + cellprobThreshold) onto cellpose-js's `chan`/`chan2` names
    // here at the engine boundary. The server consumes the original params
    // unchanged via the registry's buildRequestBody — no translation needed
    // for Run-on-Server submissions.
    //
    // GRAYSCALE       : chan=0, chan2=0 (cellpose-js auto-detects single-channel).
    // TWO_CHANNEL     : chan=cytoChannel, chan2=nucChannel (1-based passthrough).
    // THREE_CHANNEL   : same as TWO_CHANNEL — cellpose-js's SegmentOptions doesn't
    //                   expose a distinct 3-channel mode; the browser preview
    //                   uses the same (cyto, nuc) pair. The server worker handles
    //                   3-channel inputs correctly via channelMode on the request.
    const channelMode = (params.channelMode as CellPoseChannelMode) ?? CellPoseChannelMode.GRAYSCALE;
    const cytoChannel = Number(params.cytoChannel ?? 1);
    const nucChannel  = Number(params.nucChannel  ?? 0);
    const isMultiChannel = channelMode !== CellPoseChannelMode.GRAYSCALE;
    const opts: SegmentOptions = {
      // The cellprob threshold lives under dynamics options in cellpose-js
      // (mirrors cellpose.dynamics.compute_masks's signature).
      dynamics: { cellprobThreshold: Number(params.cellprobThreshold ?? 0) },
      chan:  (isMultiChannel ? cytoChannel : 0) as 0 | 1 | 2 | 3,
      chan2: (isMultiChannel ? nucChannel  : 0) as 0 | 1 | 2 | 3,
      signal,
    };
    if (diameter !== undefined && diameter > 0) opts.diameter = diameter;

    // Tile-level progress shows in the status string. When the last tile
    // finishes, cellpose-js still has ~50–100 ms of averaging + dynamics
    // to do — surface that as a distinct phase so the spinner doesn't look
    // stuck on "tile N/N".
    opts.onTileProgress = (done, total) => {
      this._status.next(
        done < total
          ? `Running inference (tile ${done} / ${total})…`
          : 'Computing flow dynamics…'
      );
    };

    this._status.next('Preprocessing image…');
    try {
      const result = await cp.segment(
        {
          data: input.data,
          width: input.width,
          height: input.height,
          channels: input.channels,
        },
        opts,
      );
      this._status.next('Rendering overlay…');
      const overlay = blendMaskOverlay(input, result.masks);
      return overlay;
    } finally {
      // Clear the status whether the call succeeded, aborted, or errored.
      this._status.next(null);
    }
  }

  dispose(): void {
    // Only cancel the in-flight run; the Cellpose instance is owned by the
    // shared CellposeSegmenterService (don't tear down a model the toolbar tool
    // may still be using).
    this.cancelCurrentOperation();
  }

  /**
   * Hand the step's params to the `cellpose-sam` JIT tool's buildRequestBody
   * unchanged.
   *
   * The engine's pipelineParams already use the exact CellPoseSamRequest
   * vocabulary (model / channelMode / cytoChannel / nucChannel / diameter /
   * cellprobThreshold), so Run-on-Server submissions don't need any
   * translation here. Kept as a hook so future engine-specific transforms
   * (e.g. normalizing channel indices, clamping ranges) have a place to live.
   */
  toServerParams(clientParams: Record<string, any>): Record<string, any> {
    return { ...clientParams };
  }
}
